import { DateInput, Select, SelectItem } from '@heroui/react'
import { getLocalTimeZone, today } from '@internationalized/date'
import PathwaySelect from 'components/Hero/PathwaySelect'
import PhoneInput from 'components/Hero/PhoneInput'
import { useAuth } from 'hooks/useAuth'
import { useFetch } from 'utils/fetch'
import { KVInput, TagsSelect } from '../inputs'

/*
 * Call-logs specific select inputs
 */
function DirectionSelect({ value, ...props }) {
  return (
    <Select aria-label="Direction" selectedKeys={[value]} {...props}>
      { }
      <SelectItem key={true}>Inbound</SelectItem>
      <SelectItem key={false}>Outbound</SelectItem>
    </Select>
  )
}

function StatusSelect({ value, ...props }) {
  return (
    <Select aria-label="Status" selectedKeys={[value]} {...props}>
      <SelectItem key="completed">Completed</SelectItem>
      <SelectItem key="failed">Failed</SelectItem>
      <SelectItem key="no-answer">No Answer</SelectItem>
      <SelectItem key="busy">Busy</SelectItem>
      <SelectItem key="canceled">Canceled</SelectItem>
      <SelectItem key={null}>Unknown</SelectItem>
    </Select>
  )
}

function AnsweredBySelect({ value, ...props }) {
  return (
    <Select
      aria-label="Answered by"
      endContent={(
        <span className="text-tiny text-nowrap text-default-400">
          for calls with
          {' '}
          <code>answer_by_enabled</code>
          {' '}
          on
        </span>
      )}
      selectedKeys={[value]}
      {...props}
    >
      <SelectItem key="voicemail">Voicemail</SelectItem>
      <SelectItem key="human">Human</SelectItem>
      <SelectItem key="no-answer">No answer</SelectItem>
      <SelectItem key="unknown">Unknown</SelectItem>
    </Select>
  )
}

function QCStatusSelect({ value, ...props }) {
  return (
    <Select aria-label="QC Status" selectedKeys={[value]} {...props}>
      <SelectItem key="pending">Pending Review</SelectItem>
      <SelectItem key="in_progress">In Progress</SelectItem>
      <SelectItem key="completed">Completed</SelectItem>
      <SelectItem key="not_needed">Not Needed</SelectItem>
    </Select>
  )
}

function AssigneeSelect({ value, ...props }) {
  const { org } = useAuth()
  const { data: orgMembers = [], isLoading } = useFetch(`/orgs/${org?.id}/members`, {
    queryKey: ['orgMembers', org?.id],
    enabled: !!org?.id,
    staleTime: Infinity,
  })

  // Find the selected member based on the value (user_id)
  const selectedMember = orgMembers.find(member => member.user_id === value)

  return (
    <Select
      aria-label="Assignee"
      placeholder="Select assignee"
      selectedKeys={value ? [value] : []}
      isLoading={isLoading}
      renderValue={() => {
        if (selectedMember) {
          return `${selectedMember.first_name} ${selectedMember.last_name}`
        }
        return null
      }}
      {...props}
    >
      {orgMembers.map(member => (
        <SelectItem key={member.user_id} value={member.user_id}>
          {member.first_name}
          {' '}
          {member.last_name}
        </SelectItem>
      ))}
    </Select>
  )
}

function QCTagSelect({ value, ...props }) {
  const { org } = useAuth()
  const qcTags = org?.preferences?.qc_tags || []

  return (
    <Select
      aria-label="QC Tag"
      placeholder="Select tag"
      selectedKeys={value ? [value] : []}
      {...props}
    >
      {qcTags.map(tag => (
        <SelectItem key={tag.label} value={tag.label}>
          {tag.label}
        </SelectItem>
      ))}
    </Select>
  )
}

function IssueSeveritySelect({ value, ...props }) {
  return (
    <Select aria-label="Issue Severity" selectedKeys={[value]} {...props}>
      <SelectItem key="high">High Severity</SelectItem>
      <SelectItem key="medium">Medium Severity</SelectItem>
      <SelectItem key="low">Low Severity</SelectItem>
    </Select>
  )
}

export const callFilterableFields = {
  inbound: { label: 'Direction', operators: ['equals'], inputComponent: DirectionSelect },
  c_id: { label: 'Call ID', operators: ['equals', 'not'] },
  to: { label: 'To', operators: ['equals', 'not'], inputComponent: PhoneInput },
  from: { label: 'From', operators: ['equals', 'not'], inputComponent: PhoneInput },
  call_length: { label: 'Duration', operators: ['equals', 'gt', 'lt', 'gte', 'lte'], inputProps: { endContent: 'minutes', type: 'number' } },
  created_at: { label: 'Created', operators: ['equals', 'gt', 'lt', 'gte', 'lte'], inputComponent: DateInput, inputProps: { defaultValue: today(getLocalTimeZone()) } },
  status: { label: 'Status', operators: ['equals', 'not'], inputComponent: StatusSelect },
  pathway_id: { label: 'Pathway', operators: ['equals', 'not'], inputComponent: PathwaySelect },
  batch_id: { label: 'Batch ID', operators: ['equals', 'not'] },
  notes: { label: 'Notes', operators: ['contains', 'isNotEmpty', 'isEmpty'] },
  pathway_tags: { label: 'Tags', operators: ['hasSome', 'none'], inputComponent: TagsSelect },
  analysis: { label: 'Analysis', operators: ['hasSome', 'none'], inputComponent: KVInput },
  variables: { label: 'Variables', operators: ['hasSome', 'none'], inputComponent: KVInput },
  citation_variables: { label: 'Citations', operators: ['hasSome', 'none'], inputComponent: KVInput },
  price: {
    label: 'Price',
    operators: ['equals', 'gt', 'lt', 'gte', 'lte'],
    inputProps: {
      startContent: (
        <div className="pointer-events-none flex items-center">
          <span className="text-small text-default-400">$</span>
        </div>
      ),
      type: 'number',
      placeholder: '0.00',
    },
  },
  answered_by: { label: 'Answered by', operators: ['equals', 'not'], inputComponent: AnsweredBySelect },
  error_message: { label: 'Error Message', operators: ['equals'] },
  qc_status: { label: 'QC Status', operators: ['equals', 'not'], inputComponent: QCStatusSelect },
  qc_assignee: { label: 'QC Assignee', operators: ['equals', 'not'], inputComponent: AssigneeSelect },
  qc_tags: { label: 'QC Tags', operators: ['hasSome', 'none'], inputComponent: QCTagSelect },
  issues: { label: 'Issue Severity', operators: ['hasSome'], inputComponent: IssueSeveritySelect },
}
